import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router";
import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { Shield, AlertTriangle, Trash2, Search, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { EmptyState } from "@/components/shared/empty-state";
import {
  deleteApiAgentsAgentIdAccessPolicy,
  getApiAgentsAgentIdAccessPolicy,
  putApiAgentsAgentIdAccessPolicy,
} from "@/lib/api/generated/access-control-agent/access-control-agent";
import { useGetApiChannelConnections } from "@/lib/api/generated/channels/channels";
import { ControlApiError } from "@/lib/api/client";
import { useTenantDirectoryUsersQuery } from "@/lib/query/tenant-app-users";
import { queryKeys } from "@/lib/query/keys";
import { toastMutationError } from "@/lib/utils/formatting";
import { useDocumentTitle } from "@/lib/hooks/use-document-title";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import { useAgentEditorDirty } from "@/stores/agent-editor-ui-store";
import type { AgentAccessPolicyRecord } from "../../../../../packages/shared-types/src/index.js";
import { AgentTabSurface } from "./agent-tab-surface";

type AccessMode = AgentAccessPolicyRecord["accessMode"];

export function AgentAccessTab() {
  const { agentId } = useParams<{ agentId: string }>();
  const { tenantId } = useTenantContext();
  const queryClient = useQueryClient();
  const { data: policy, isLoading } = useQuery({
    queryKey: queryKeys.agentAccessPolicy(tenantId ?? "", agentId ?? ""),
    queryFn: async () => {
      try {
        return await getApiAgentsAgentIdAccessPolicy(
          agentId!,
          { tenantId: tenantId! },
        ) as AgentAccessPolicyRecord;
      } catch (error) {
        if (error instanceof ControlApiError && error.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: Boolean(tenantId && agentId),
  });
  const { data: directoryUsers } = useTenantDirectoryUsersQuery(tenantId!);
  const tenantScopedQueryOptions = { query: { enabled: Boolean(tenantId) } };
  const { data: channelConnections } = useGetApiChannelConnections(tenantScopedQueryOptions);
  const saveMutation = useMutation({
    mutationFn: (record: AgentAccessPolicyRecord) =>
      putApiAgentsAgentIdAccessPolicy(
        agentId!,
        { record },
      ) as Promise<AgentAccessPolicyRecord>,
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.agentAccessPolicy(input.tenantId, input.agentId),
      });
    },
  });
  const deleteMutation = useMutation({
    mutationFn: (input: { tenantId: string; agentId: string }) =>
      deleteApiAgentsAgentIdAccessPolicy(input.agentId, {}, { tenantId: input.tenantId }),
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.agentAccessPolicy(input.tenantId, input.agentId),
      });
    },
  });
  useDocumentTitle("Access");

  const [editing, setEditing] = useState(false);
  const [accessMode, setAccessMode] = useState<AccessMode>("all_tenant_users");
  const [allowedUserIds, setAllowedUserIds] = useState<string[]>([]);
  const [allowedChannels, setAllowedChannels] = useState<string[]>([]);
  const [notes, setNotes] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUserPicker, setShowUserPicker] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [userPage, setUserPage] = useState(0);
  const USERS_PER_PAGE = 50;

  // Sync from loaded policy
  useEffect(() => {
    if (policy) {
      setAccessMode(policy.accessMode);
      setAllowedUserIds(policy.allowedUserIds);
      setAllowedChannels(policy.allowedChannels);
      setNotes(policy.notes ?? "");
      setEditing(true);
    } else {
      setEditing(false);
    }
  }, [policy]);

  // Dirty state
  const isDirty = useMemo(() => {
    if (!editing) {return false;}
    if (!policy) {return editing;} // creating new — always dirty if editing
    return (
      accessMode !== policy.accessMode ||
      JSON.stringify([...allowedUserIds].toSorted()) !== JSON.stringify([...policy.allowedUserIds].toSorted()) ||
      JSON.stringify([...allowedChannels].toSorted()) !== JSON.stringify([...policy.allowedChannels].toSorted()) ||
      (notes.trim() || "") !== (policy.notes?.trim() || "")
    );
  }, [editing, policy, accessMode, allowedUserIds, allowedChannels, notes]);

  useAgentEditorDirty("access", isDirty);

  const handleDiscard = useCallback(() => {
    if (policy) {
      setAccessMode(policy.accessMode);
      setAllowedUserIds(policy.allowedUserIds);
      setAllowedChannels(policy.allowedChannels);
      setNotes(policy.notes ?? "");
    } else {
      setEditing(false);
    }
  }, [policy]);

  // Derive assigned users for the operator-facing picker.
  const tenantUsers = useMemo(() => {
    return (directoryUsers?.items ?? [])
      .map((user) => ({
        userId: user.id,
        displayName: user.displayName,
        email: user.email,
      }))
      .toSorted((a, b) =>
        (a.displayName || a.userId).localeCompare(b.displayName || b.userId),
      );
  }, [directoryUsers]);

  const selectedUsers = useMemo(() => {
    const selected = new Set(allowedUserIds);
    return tenantUsers.filter((user) => selected.has(user.userId));
  }, [allowedUserIds, tenantUsers]);

  const filteredUsers = useMemo(() => {
    if (!userSearch) {
      return tenantUsers;
    }
    const normalizedSearch = userSearch.toLowerCase();
    return tenantUsers.filter((user) =>
      user.displayName.toLowerCase().includes(normalizedSearch) ||
      user.email.toLowerCase().includes(normalizedSearch),
    );
  }, [tenantUsers, userSearch]);

  const toggleUser = useCallback((userId: string) => {
    setAllowedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  }, []);

  const channelTypeSuggestions = useMemo(
    () => [...new Set(channelConnections?.map((c) => c.channelType).filter(Boolean) ?? [])],
    [channelConnections],
  );

  const handleSave = async () => {
    const record: AgentAccessPolicyRecord = {
      tenantId: tenantId!,
      agentId: agentId!,
      accessMode,
      allowedUserIds: [...new Set(allowedUserIds.map((s) => s.trim()).filter(Boolean))],
      allowedChannels: [...new Set(allowedChannels.map((s) => s.trim()).filter(Boolean))],
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    };

    try {
      await saveMutation.mutateAsync(record);
      toast.success("Access policy saved");
    } catch (err) {
      toast.error(toastMutationError(err, "Failed to save access policy"));
    }
  };

  const handleDelete = async () => {
    setShowDeleteConfirm(false);
    try {
      await deleteMutation.mutateAsync({ tenantId: tenantId!, agentId: agentId! });
      toast.success("Access policy deleted");
    } catch (err) {
      toast.error(toastMutationError(err, "Failed to delete access policy"));
    }
  };

  const handleStartCreate = () => {
    setAccessMode("all_tenant_users");
    setAllowedUserIds([]);
    setAllowedChannels([]);
    setNotes("");
    setEditing(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>Loading...</p>
      </div>
    );
  }

  // Empty state — no policy exists and user hasn't started creating one
  if (!policy && !editing) {
    return (
      <EmptyState
        icon={<Shield size={40} strokeWidth={1.2} />}
        title="No access policy"
        description="This agent is currently accessible to all assigned users. Create an access policy to restrict who can use this agent."
        action={{ label: "Create Access Policy", onClick: handleStartCreate }}
      />
    );
  }

  const isDenyAll = accessMode === "allow_list" && allowedUserIds.length === 0;

  return (
    <AgentTabSurface
      title="Access"
      description="Choose who can use this agent and which channels are allowed."
      actions={
        <>
          {isDirty && (
            <Button variant="ghost" onClick={handleDiscard}>
              Discard
            </Button>
          )}
          <Button onClick={handleSave} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? "Saving..." : policy ? "Update Policy" : "Create Policy"}
          </Button>
          {policy && (
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={deleteMutation.isPending}
              className="text-danger"
            >
              <Trash2 size={14} />
              Delete Policy
            </Button>
          )}
        </>
      }
    >
    <div className="flex max-w-5xl flex-col gap-5">
      {/* Effective access summary */}
      <section className="rounded-[var(--radius-md)] bg-[var(--bg-elevated)] px-4 py-3">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className="flex items-center gap-3">
            <div className="flex size-8 items-center justify-center rounded-[var(--radius-md)] bg-[var(--bg-subtle)]">
              <Shield size={16} style={{ color: "var(--text-strong)" }} />
            </div>
            <div>
              <div className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                Access
              </div>
              <div className="text-sm font-medium" style={{ color: "var(--text-strong)" }}>
                {accessMode === "all_tenant_users"
                  ? "All assigned users"
                  : `${allowedUserIds.length} allowed user${allowedUserIds.length !== 1 ? "s" : ""}`}
              </div>
            </div>
          </div>
          <div>
            <div className="text-xs" style={{ color: "var(--muted-foreground)" }}>
              Channels
            </div>
            <div className="text-sm font-medium" style={{ color: "var(--text-strong)" }}>
              {allowedChannels.length === 0 ? "All channels" : allowedChannels.join(", ")}
            </div>
          </div>
        </div>
      </section>

      {/* Deny-all warning */}
      {isDenyAll && (
        <Alert className="border-transparent bg-[var(--warn-subtle)] text-[var(--warn)]">
          <AlertTriangle size={16} />
          <AlertDescription className="text-[var(--warn)]">
            Allow list is empty. This effectively denies all users access to this agent.
          </AlertDescription>
        </Alert>
      )}

      {/* Access mode */}
      <section className="rounded-[var(--radius-md)] bg-[var(--bg-elevated)] p-4">
        <h3 className="mb-3 text-sm font-semibold" style={{ color: "var(--text-strong)" }}>
          Access Mode
        </h3>
          <RadioGroup
            value={accessMode}
            onValueChange={(value) => setAccessMode(value as AccessMode)}
            className="grid gap-3 md:grid-cols-2"
          >
            <Label
              htmlFor="access-mode-all"
              className="flex cursor-pointer items-start gap-3 rounded-[var(--radius-md)] border px-4 py-3 transition-colors hover:bg-[var(--bg-hover)]"
              style={{ borderColor: accessMode === "all_tenant_users" ? "var(--accent)" : "var(--border)" }}
            >
              <RadioGroupItem id="access-mode-all" value="all_tenant_users" className="mt-0.5" />
              <div>
                <div className="text-sm font-medium" style={{ color: "var(--text-strong)" }}>
                  All Assigned Users
                </div>
                <div className="mt-1 text-sm" style={{ color: "var(--muted-foreground)" }}>
                  Any user assigned to Agentiq can use the agent.
                </div>
              </div>
            </Label>
            <Label
              htmlFor="access-mode-allow-list"
              className="flex cursor-pointer items-start gap-3 rounded-[var(--radius-md)] border px-4 py-3 transition-colors hover:bg-[var(--bg-hover)]"
              style={{ borderColor: accessMode === "allow_list" ? "var(--accent)" : "var(--border)" }}
            >
              <RadioGroupItem id="access-mode-allow-list" value="allow_list" className="mt-0.5" />
              <div>
                <div className="text-sm font-medium" style={{ color: "var(--text-strong)" }}>
                  Allow List
                </div>
                <div className="mt-1 text-sm" style={{ color: "var(--muted-foreground)" }}>
                  Only listed assigned users can use the agent.
                </div>
              </div>
            </Label>
          </RadioGroup>
      </section>

      {/* Allowed users (only when allow_list) */}
      {accessMode === "allow_list" && (() => {
        const totalPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);
        const pageUsers = filteredUsers.slice(userPage * USERS_PER_PAGE, (userPage + 1) * USERS_PER_PAGE);

        return (
          <>
            <section className="rounded-[var(--radius-md)] bg-[var(--bg-elevated)] p-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold" style={{ color: "var(--text-strong)" }}>
                  Allowed Assigned Users
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setShowUserPicker(true); setUserSearch(""); setUserPage(0); }}
                  disabled={tenantUsers.length === 0}
                >
                  <Users size={14} />
                  Manage Users
                </Button>
              </div>
                {tenantUsers.length === 0 ? (
                  <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                    No assigned users are available for this tenant yet.
                  </p>
                ) : allowedUserIds.length === 0 ? (
                  <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                    No assigned users selected. Click "Manage Users" to choose who can access this agent.
                  </p>
                ) : (
                  <div className="flex flex-col gap-2">
                    <p className="text-sm" style={{ color: "var(--text-strong)" }}>
                      <span className="font-semibold">{allowedUserIds.length}</span>
                      <span style={{ color: "var(--muted-foreground)" }}> of {tenantUsers.length} assigned users allowed</span>
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {selectedUsers.slice(0, 6).map((user) => (
                        <span
                          key={user.userId}
                          className="rounded-[var(--radius-sm)] bg-[var(--bg-subtle)] px-2 py-1 text-xs"
                          style={{ color: "var(--muted-foreground)" }}
                        >
                          {user.displayName}
                        </span>
                      ))}
                      {selectedUsers.length > 6 ? (
                        <span
                          className="rounded-[var(--radius-sm)] bg-[var(--bg-subtle)] px-2 py-1 text-xs"
                          style={{ color: "var(--muted-foreground)" }}
                        >
                          +{selectedUsers.length - 6} more
                        </span>
                      ) : null}
                    </div>
                  </div>
                )}
            </section>

            {/* User picker dialog */}
            <Dialog open={showUserPicker} onOpenChange={setShowUserPicker}>
              <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
                <DialogHeader>
                  <DialogTitle>
                    Select Allowed Users
                    <span className="text-xs font-normal ml-2" style={{ color: "var(--muted-foreground)" }}>
                      {allowedUserIds.length} selected
                    </span>
                  </DialogTitle>
                </DialogHeader>

                {/* Search + Select All */}
                <div className="flex items-center gap-3 py-2">
                  <div className="relative flex-1">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--muted-foreground)" }} />
                    <Input
                      value={userSearch}
                      onChange={(e) => { setUserSearch(e.target.value); setUserPage(0); }}
                      placeholder="Search by name or email..."
                      className="h-8 pl-8 text-xs"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const visibleIds = filteredUsers.map((u) => u.userId);
                      const allSelected = visibleIds.every((id) => allowedUserIds.includes(id));
                      if (allSelected) {
                        setAllowedUserIds(allowedUserIds.filter((id) => !visibleIds.includes(id)));
                      } else {
                        setAllowedUserIds([...new Set([...allowedUserIds, ...visibleIds])]);
                      }
                    }}
                  >
                    {filteredUsers.every((u) => allowedUserIds.includes(u.userId))
                      ? "Deselect All"
                      : "Select All"}
                  </Button>
                </div>

                <div className="flex-1 overflow-y-auto rounded-[var(--radius-md)] border border-border">
                  <Table>
                    <TableHeader className="sticky top-0 bg-[var(--bg-subtle)]">
                      <TableRow>
                        <TableHead className="w-10" />
                        <TableHead>User</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pageUsers.map((user) => {
                        const checked = allowedUserIds.includes(user.userId);
                        return (
                          <TableRow
                            key={user.userId}
                            className="cursor-pointer"
                            onClick={() => toggleUser(user.userId)}
                          >
                            <TableCell className="text-center">
                              <Checkbox checked={checked} readOnly className="pointer-events-none" />
                            </TableCell>
                            <TableCell>
                              <div className="space-y-0.5">
                                <div className="font-medium" style={{ color: "var(--text-strong)" }}>
                                  {user.displayName || user.email || "Unnamed user"}
                                </div>
                                <div className="text-sm" style={{ color: "var(--muted-foreground)" }}>
                                  {user.email || "No email"}
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {pageUsers.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={2} className="h-24 text-center" style={{ color: "var(--muted-foreground)" }}>
                            No users match your search.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-2 text-xs" style={{ color: "var(--muted-foreground)" }}>
                    <span>
                      Page {userPage + 1} of {totalPages} ({filteredUsers.length} users)
                    </span>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" disabled={userPage === 0} onClick={() => setUserPage((p) => p - 1)}>
                        Previous
                      </Button>
                      <Button variant="outline" size="sm" disabled={userPage >= totalPages - 1} onClick={() => setUserPage((p) => p + 1)}>
                        Next
                      </Button>
                    </div>
                  </div>
                )}

                <DialogFooter>
                  <Button onClick={() => setShowUserPicker(false)}>Done</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        );
      })()}

      {/* Allowed channels */}
      <section className="rounded-[var(--radius-md)] bg-[var(--bg-elevated)] p-4">
        <h3 className="mb-3 text-sm font-semibold" style={{ color: "var(--text-strong)" }}>
          Allowed Channels
        </h3>
          {channelTypeSuggestions.length === 0 ? (
            <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
              No channel connections configured for this tenant.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap gap-2">
                {channelTypeSuggestions.map((ch) => {
                  const checked = allowedChannels.includes(ch);
                  return (
                    <label
                      key={ch}
                      htmlFor={`access-channel-${ch}`}
                      className="flex cursor-pointer items-center gap-2 rounded-[var(--radius-md)] border px-3 py-2 text-sm transition-colors hover:bg-[var(--bg-hover)]"
                      style={{
                        borderColor: checked ? "var(--accent)" : "var(--border)",
                        color: checked ? "var(--text-strong)" : "var(--text)",
                      }}
                    >
                      <Checkbox
                        id={`access-channel-${ch}`}
                        checked={checked}
                        onCheckedChange={() =>
                          setAllowedChannels((prev) =>
                            prev.includes(ch) ? prev.filter((c) => c !== ch) : [...prev, ch],
                          )
                        }
                        className="shrink-0"
                      />
                      {ch}
                    </label>
                  );
                })}
              </div>
              <p className="text-sm" style={{ color: "var(--muted-foreground)" }}>
                {allowedChannels.length === 0
                  ? "No channel restriction. The agent is accessible from every configured channel."
                  : `Restricted to: ${allowedChannels.join(", ")}`}
              </p>
            </div>
          )}
      </section>

      {/* Notes */}
      <section className="rounded-[var(--radius-md)] bg-[var(--bg-elevated)] p-4">
        <h3 className="mb-3 text-sm font-semibold" style={{ color: "var(--text-strong)" }}>
          Notes
        </h3>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Optional operator notes about this access policy..."
            className="resize-y bg-[var(--bg-elevated)]"
            style={{ color: "var(--text)" }}
          />
      </section>

      <ConfirmDialog
        open={showDeleteConfirm}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        title="Delete access policy?"
        description="Deleting the access policy returns this agent to default unrestricted access. All assigned users will be able to use the agent."
        confirmLabel="Delete"
        variant="destructive"
      />
    </div>
    </AgentTabSurface>
  );
}
